import { useEffect, useState } from 'react';
import type { DialectDescriptor } from '@bits2schema/shared';
import { api } from './api/client';
import { useAuth } from './auth/AuthContext';
import { LoginPage } from './auth/LoginPage';
import { GreenfieldDialog } from './components/GreenfieldDialog';
import { ImportWizard } from './components/ImportWizard';
import { PreferencesToolbar } from './components/PreferencesToolbar';
import { ProjectListPage } from './components/ProjectListPage';
import { ProjectPage } from './components/ProjectPage';
import { UserBar } from './components/UserBar';
import { useT } from './i18n';
import { describeError } from './utils/format';

type View =
  | { kind: 'projects' }
  | { kind: 'import' }
  | { kind: 'project'; projectId: string };

export function App() {
  const t = useT();
  const { status } = useAuth();
  const [view, setView] = useState<View>({ kind: 'projects' });
  const [greenfieldOpen, setGreenfieldOpen] = useState(false);
  const [dialects, setDialects] = useState<DialectDescriptor[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const loggedIn = status.kind === 'loggedIn';

  useEffect(() => {
    if (!loggedIn) return;
    api
      .listDialects()
      .then((response) => setDialects(response.dialects))
      .catch((caught: unknown) => setError(describeError(caught, t)));
  }, [t, loggedIn]);

  const openProject = (projectId: string) => {
    setGreenfieldOpen(false);
    setView({ kind: 'project', projectId });
  };

  const backToProjects = () => {
    setRefreshToken((value) => value + 1);
    setView({ kind: 'projects' });
  };

  if (status.kind === 'checking') {
    return <p className="hint">{t('common.loading')}</p>;
  }

  if (status.kind === 'loggedOut') {
    return <LoginPage />;
  }

  return (
    <>
      <header className="app-header">
        <span className="app-header__title">{t('app.title')}</span>
        <span className="app-header__subtitle">{t('app.subtitle')}</span>
        <UserBar />
        <PreferencesToolbar />
      </header>

      <main className="app-main">
        {error && <div className="notice notice--error">{error}</div>}

        {view.kind === 'projects' && (
          <ProjectListPage
            dialects={dialects}
            refreshToken={refreshToken}
            onOpenProject={openProject}
            onNewImport={() => setView({ kind: 'import' })}
            onNewGreenfield={() => setGreenfieldOpen(true)}
          />
        )}

        {view.kind === 'import' && (
          <ImportWizard
            dialects={dialects}
            onCancel={backToProjects}
            onImported={openProject}
          />
        )}

        {view.kind === 'project' && (
          <ProjectPage projectId={view.projectId} dialects={dialects} onBack={backToProjects} />
        )}

        {greenfieldOpen && (
          <GreenfieldDialog
            dialects={dialects}
            onCancel={() => setGreenfieldOpen(false)}
            onCreated={openProject}
          />
        )}
      </main>
    </>
  );
}
